import { useRef, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { EquipmentDialog } from "@/components/EquipmentDialog";
import { LogHoursDialog } from "@/components/LogHoursDialog";
import { ReminderDialog } from "@/components/ReminderDialog";
import { CompleteReminderDialog } from "@/components/CompleteReminderDialog";
import { demoApi, problemMessage } from "@/api/client";
import { useEquipment, useOpenReminders } from "@/hooks/useEquipment";
import { formatHours, reminderPhrase } from "@/lib/reminderPhrase";
import type { Equipment as EquipmentRow, Reminder } from "@/api/types";
import "./Equipment.css";

type DialogState =
  | { kind: "equipment"; equipment: EquipmentRow | null }
  | { kind: "hours"; equipment: EquipmentRow }
  | { kind: "reminder"; equipment: EquipmentRow }
  | { kind: "done"; equipment: EquipmentRow; reminder: Reminder }
  | null;

/**
 * ef-061-8 — the equipment page, `/farm/equipment`. A PAGE, not a Farm tab (Farm is at the ADR 0009
 * five-tab ceiling): the machines in name order, each with its current hours, buttons to log hours / add a
 * reminder / edit / archive, and under it that machine's open reminders with a "Mark done" button.
 * "Show archived" adds archived machines (hidden, never deleted) with a Restore button.
 *
 * Every write re-reads what the page shows — the server's answer is the truth, nothing is patched in
 * place; logging hours re-reads the reminders too (one can flip to due at once, and the due sentence
 * counts from the new reading). The reminder sentence is `reminderPhrase()`, shared with the Home tile.
 *
 * State in the DOM: the one `<main>` carries `data-state` = loading | error | empty | ready; ONE
 * `role="alert"` carries a failed read or a failed Archive / Restore. A LIST of machines, not a table —
 * it has to read on a phone.
 */
export function Equipment() {
  const [showArchived, setShowArchived] = useState(false);
  const machines = useEquipment(showArchived);
  const reminders = useOpenReminders();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  const items = machines.items;
  const readFailed = machines.status === "error" || reminders.status === "error";
  const state = readFailed ? "error" : items === null ? "loading" : items.length === 0 ? "empty" : "ready";
  const alertText = readFailed ? (machines.error ?? reminders.error ?? "Could not load equipment.") : actionError;

  function refreshAll(): void {
    machines.refetch();
    reminders.refetch();
  }

  function openDialog(next: DialogState, trigger: HTMLElement): void {
    triggerRef.current = trigger;
    setDialog(next);
  }

  function closeDialog(): void {
    setDialog(null);
    // The dialog unmounts with the state change; hand focus back to what opened it (if it still exists).
    const trigger = triggerRef.current;
    requestAnimationFrame(() => trigger?.isConnected && trigger.focus());
  }

  async function change(machine: EquipmentRow, run: () => Promise<unknown>, failure: string): Promise<void> {
    if (busyId !== null) return;
    setBusyId(machine.id);
    setActionError(null);
    try {
      await run();
      refreshAll();
    } catch (err) {
      setActionError(problemMessage(err, failure));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <main className="equipment-page" data-state={state} aria-busy={state === "loading"}>
      <PageHeader
        title="Equipment"
        subtitle="Machines, their hour-meter readings, and the service they are due for."
        action={
          <button type="button" className="btn btn--primary" onClick={(e) => openDialog({ kind: "equipment", equipment: null }, e.currentTarget)}>
            Add equipment
          </button>
        }
      />

      <div className="equipment-page__tools">
        <label className="equipment-page__toggle">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          Show archived
        </label>
      </div>

      {alertText && (
        <div className="equipment-page__error" role="alert">
          <p>{alertText}</p>
        </div>
      )}
      {state === "loading" && <StateMessage tone="loading" title="Loading equipment…" />}
      {state === "empty" && <StateMessage tone="empty" title="No equipment yet." detail="Add a machine to log its hours and set service reminders." />}

      {!readFailed && items !== null && items.length > 0 && (
        <ul className="equipment-list" aria-label="Equipment">
          {items.map((m) => {
            const archived = Boolean(m.archivedAt);
            const openReminders = reminders.byEquipment.get(m.id) ?? [];
            const detail = [m.makeModel, m.serialNumber ? `Serial ${m.serialNumber}` : null].filter(Boolean).join(" · ");
            return (
              <li key={m.id} className="equipment-item" data-archived={archived}>
                <div className="equipment-item__head">
                  <div className="equipment-item__main">
                    <span className="equipment-item__name">
                      {m.name}
                      {archived && <span className="equipment-item__badge">Archived</span>}
                    </span>
                    {detail && <span className="equipment-item__detail">{detail}</span>}
                    <span className="equipment-item__hours">
                      {m.currentHours === null || m.currentHours === undefined ? "No readings yet" : `${formatHours(m.currentHours)} h`}
                    </span>
                  </div>
                  <div className="equipment-item__actions">
                    {archived ? (
                      <button
                        type="button"
                        className="btn btn--ghost"
                        disabled={busyId !== null}
                        onClick={() => void change(m, () => demoApi.restoreEquipment(m.id), `Could not restore ${m.name}.`)}
                      >
                        {`Restore ${m.name}`}
                      </button>
                    ) : (
                      <>
                        <button type="button" className="btn btn--ghost" onClick={(e) => openDialog({ kind: "hours", equipment: m }, e.currentTarget)}>
                          {`Log hours for ${m.name}`}
                        </button>
                        <button type="button" className="btn btn--ghost" onClick={(e) => openDialog({ kind: "reminder", equipment: m }, e.currentTarget)}>
                          {`Add reminder for ${m.name}`}
                        </button>
                        <button type="button" className="btn btn--ghost" onClick={(e) => openDialog({ kind: "equipment", equipment: m }, e.currentTarget)}>
                          {`Edit ${m.name}`}
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost"
                          disabled={busyId !== null}
                          onClick={() => void change(m, () => demoApi.archiveEquipment(m.id), `Could not archive ${m.name}.`)}
                        >
                          {`Archive ${m.name}`}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {!archived && reminders.status === "ready" && (
                  <>
                    <ul className="equipment-reminders" aria-label={`Reminders for ${m.name}`}>
                      {openReminders.map((r) => (
                        <li key={r.id} className="equipment-reminder" data-state={r.state}>
                          <div className="equipment-reminder__main">
                            <span className="equipment-reminder__title">{r.title}</span>
                            <span className="equipment-reminder__phrase">{reminderPhrase(r)}</span>
                          </div>
                          <button
                            type="button"
                            className="btn btn--ghost"
                            onClick={(e) => openDialog({ kind: "done", equipment: m, reminder: r }, e.currentTarget)}
                          >
                            {`Mark done ${r.title}`}
                          </button>
                        </li>
                      ))}
                    </ul>
                    {openReminders.length === 0 && <p className="equipment-item__none">No open reminders.</p>}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {dialog?.kind === "equipment" && (
        <EquipmentDialog key={dialog.equipment?.id ?? "new"} equipment={dialog.equipment} onClose={closeDialog} onSaved={refreshAll} />
      )}
      {dialog?.kind === "hours" && <LogHoursDialog key={dialog.equipment.id} equipment={dialog.equipment} onClose={closeDialog} onSaved={refreshAll} />}
      {dialog?.kind === "reminder" && <ReminderDialog key={dialog.equipment.id} equipment={dialog.equipment} onClose={closeDialog} onSaved={refreshAll} />}
      {dialog?.kind === "done" && (
        <CompleteReminderDialog
          key={dialog.reminder.id}
          reminder={dialog.reminder}
          currentHours={dialog.equipment.currentHours ?? null}
          onClose={closeDialog}
          onSaved={refreshAll}
        />
      )}
    </main>
  );
}
